import React, { useEffect, useState, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { noop } from 'lodash';
import { useEditor } from '@src/hooks/useEditor';
import TextEditor from '@src/components/TextEditor';
import { Colors } from '@ateams/components';
import { Icon, IconType } from '@ateams/components';

interface DescriptionProps {
  description: string;
  showReadMore?: boolean;
}

interface StyleProps {
  isExpanded: boolean;
  contentHeight: number;
  showReadMore: boolean;
  needsReadMore: boolean;
}

const useStyles = createUseStyles({
  container: {
    marginTop: 0,
  },
  editorContainer: {
    transition: (props: StyleProps) =>
      props.showReadMore ? 'height 0.3s' : 'none',
    pointerEvents: 'none',
    height: (props: StyleProps) =>
      !props.showReadMore || !props.needsReadMore || props.isExpanded
        ? `${props.contentHeight + 10}px`
        : `${DEFAULT_HEIGHT}px`,
    overflow: 'hidden',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '30px',
      background:
        'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0.8) 60%, rgba(255,255,255,1))',
      opacity: (props: StyleProps) =>
        !props.showReadMore || !props.needsReadMore || props.isExpanded ? 0 : 1,
      pointerEvents: 'none',
    },
  },
  readMoreButtonContainer: {
    marginTop: 8,
  },
  readMoreButton: {
    color: Colors.secondaryDark,
    cursor: 'pointer',
    transition: 'opacity 0.2s ease-in-out',
    fontSize: 14,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    '&:hover': {
      opacity: 0.8,
    },
  },
  icon: {
    width: 12,
  },
});

const DEFAULT_HEIGHT = 110;

const Description = ({
  description,
  showReadMore = false,
}: DescriptionProps): JSX.Element | null => {
  const [isExpanded, setIsExpanded] = useState(!showReadMore);
  const [contentHeight, setContentHeight] = useState(1000);
  const [needsReadMore, setNeedsReadMore] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const styles = useStyles({
    isExpanded,
    contentHeight,
    showReadMore,
    needsReadMore,
  });
  const { editor } = useEditor({
    initialValue: description,
    onDescriptionChange: noop,
    readOnly: true,
    noTitle: true,
  });

  // Measure content height after rendering and when description changes
  useEffect(() => {
    if (!editor || !contentRef.current) return;

    // Set editor content when description changes
    if (description) {
      editor.commands.setContent(description);
    }

    // Use timeout to ensure content is fully rendered by the text editor
    const timeoutId = setTimeout(() => {
      const height = contentRef.current?.scrollHeight || 0;
      setContentHeight(height);
      // Determine if we need the read more button
      setNeedsReadMore(height > DEFAULT_HEIGHT);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [description, editor, needsReadMore]);

  const toggleExpand = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Force re-measure when expanding
    if (!isExpanded && contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }

    setIsExpanded(!isExpanded);
  };

  if (!editor) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.editorContainer}>
        <div ref={contentRef}>
          <TextEditor readOnly editor={editor} />
        </div>
      </div>
      {showReadMore && needsReadMore && (
        <div className={styles.readMoreButtonContainer}>
          <a className={styles.readMoreButton} href="/" onClick={toggleExpand}>
            {isExpanded ? (
              <>
                Read less
                <Icon
                  type={IconType.ArrowUpSecondaryDark}
                  className={styles.icon}
                />
              </>
            ) : (
              <>
                Read more
                <Icon
                  type={IconType.ArrowDownSecondaryDark}
                  className={styles.icon}
                />
              </>
            )}
          </a>
        </div>
      )}
    </div>
  );
};

export default Description;
